import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
  KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
} from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import {
  Order,
  getValueFromName,
  sleep,
  stableSort,
  titleStr,
} from "../lib/helper";
import "../styles/table.list.css";
import {
  Badge,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  TextField,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { HeadCell, TableApiRes } from "../models/base.model";
import TableActions, { ActionItem } from "./TableActions";
import TableAddButton from "./TableAddButton";
import TableFilter, {
  FilterGroup,
  FilterItem,
  FilterType,
} from "./TableFilter";
import moment, { Moment } from "moment";
import NoResult from "./NoResult";
import { uniqBy } from "lodash";
import { useConfig } from "../containers/ConfigContext";

const NoBorderTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: 0,
}));

interface EnhancedTableProps {
  onRequestSort: (property: string, event?: React.MouseEvent<unknown>) => void;
  order: Order;
  orderBy: string;
  headCells: readonly HeadCell[];
  noAction?: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, noAction } = props;
  const { localized } = useConfig();
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(property, event);
    };

  return (
    <TableHead>
      <TableRow>
        {props.headCells.map((headCell) => (
          <NoBorderTableCell
            className="head-th"
            key={headCell.id}
            width={headCell.width}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label || titleStr(headCell.id)}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </NoBorderTableCell>
        ))}
        {!noAction && (
          <NoBorderTableCell width={32} className="head-th">
            {localized("Actions")}
          </NoBorderTableCell>
        )}
      </TableRow>
    </TableHead>
  );
}

interface BaseEventProps<T> {
  onItemClick?: (item: T, event?: React.MouseEvent<unknown>) => void;
  onLoad: () => Promise<TableApiRes<T>>;
  onSearch?: (value: string, all: T[]) => Promise<TableApiRes<T>>;
  onAdd?: () => Promise<T | T[]>;
  onAddBtnText?: string;
  onEdit?: (record: T) => Promise<T>;
  onDelete?: (record: T) => Promise<void | string[]>;
  noAction?: boolean;
  searchText?: string;
  filters?: FilterGroup<T>[];
  initialSortKey?: string;
  initialOrder?: Order;
  uid: keyof T;
  addButtonClassName?: string;
  hideRefresh?: boolean;
  onAllDataChanged?: (data: T[]) => void;
  actions?: (row: T) => ActionItem[];
}

interface TableProps<T> extends BaseEventProps<T> {
  headCells: readonly HeadCell[];
}

interface GridProps<T> extends BaseEventProps<T> {
  onItemClick?: (item: T, event?: React.MouseEvent<unknown>) => void;
  renderItem: JSX.Element;
  sorts: (keyof T)[];
}

interface TableFilterRef {
  open: () => void;
}

let searchValue = "";

const EnhancedTable = React.forwardRef(
  <T extends unknown>(props: TableProps<T> | GridProps<T>, ref?: any) => {
    const [order, setOrder] = React.useState<Order>(
      props.initialOrder || "desc"
    );
    const [rows, setRows] = React.useState<T[]>();
    const [all, setAll] = React.useState<T[]>([]);
    const [orderBy, setOrderBy] = React.useState(
      props.initialSortKey ||
        ("headCells" in props
          ? props.headCells[0].id
          : props.sorts[0]?.toString())
    );
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(
      "headCells" in props ? 10 : 12
    );
    const [loading, setLoading] = React.useState(true);
    const [filterValue, setFilterValue] = React.useState();
    const filterRef = React.useRef<TableFilterRef>(null);
    const { localized } = useConfig();
    const onSearch = async () => {
      if (!props.onSearch) {
        return;
      }
      try {
        setLoading(true);
        await sleep();
        const { data } = await props.onSearch(searchValue, all);
        setRows(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await props.onLoad();
        setAll([...data]);
        setRows([...data]);
        setPage(0);
        setLoading(false);
      } catch (error) {
        setRows([]);
        setPage(0);
        setLoading(false);
        console.error(error);
      }
    };

    React.useImperativeHandle(ref, () => ({
      onDelete(record: T) {
        updateData(record, true);
      },
      onRefresh(records?: T[]) {
        if (records) {
          setAll([...records]);
          setRows([...records]);
        } else {
          fetchData();
        }
      },
      onEdit(record: T) {
        updateData(record);
      },
    }));

    React.useEffect(() => {
      fetchData();
    }, []);

    React.useEffect(() => {
      if (props.onAllDataChanged && !loading) {
        props.onAllDataChanged(all);
      }
    }, [all]);

    const handleRequestSort = (property: string) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const onAdd = async () => {
      if (!props.onAdd) {
        return;
      }
      const record = await props.onAdd();
      if (!record) {
        return;
      }
      const res = Array.isArray(record) ? record : [record];
      setPage(0);
      setAll(uniqBy(res.concat(all || []), props.uid));
      setRows(uniqBy(res.concat(rows || []), props.uid));
    };

    const onEdit = async (record: T) => {
      if (!props.onEdit) {
        return;
      }
      const row = await props.onEdit(record);
      updateData(row);
    };

    const updateData = (row: T, remove?: boolean) => {
      const i = all.findIndex((a) => a[props.uid] === row[props.uid]);
      const k = (rows || []).findIndex((a) => a[props.uid] === row[props.uid]);
      if (i >= 0 && k >= 0) {
        remove ? all.splice(i, 1) : all.splice(i, 1, row);
        remove ? rows?.splice(k, 1) : rows?.splice(k, 1, row);
        setAll([...(all || [])]);
        setRows([...(rows || [])]);
      }
    };

    const onDelete = async (record: T) => {
      if (!props.onDelete) {
        return;
      }
      const res = await props.onDelete(record);
      if (res && Array.isArray(res)) {
        for (const val of res) {
          const v: any = {};
          v[props.uid] = val;
          updateData(v, true);
        }
      } else {
        updateData(record, true);
      }
    };

    const onFilter = async (value: any) => {
      setFilterValue(value);
      if (value && props.filters) {
        const data = [...all];
        const filterItems = props.filters.reduce(
          (a, f) => a.concat(f.items),
          [] as FilterItem<T>[]
        );
        const filtered = data.filter((d) => {
          return filterItems.every((filterCondition) => {
            const v = value[filterCondition.name];
            if (v) {
              if (filterCondition.type === FilterType.date_range) {
                let bigger = true,
                  less = true;
                if (v.from) {
                  bigger =
                    moment(d[filterCondition.name] as any).dayOfYear() >=
                    (v.from as Moment).dayOfYear();
                }
                if (v.to) {
                  less =
                    moment(d[filterCondition.name] as any).dayOfYear() <=
                    (v.to as Moment).dayOfYear();
                }
                return bigger && less;
              } else if (filterCondition.type === FilterType.selection) {
                return (
                  v.length === 0 ||
                  v.indexOf((d[filterCondition.name] as any).toString()) > -1
                );
              }
            } else {
              return true;
            }
          });
        });
        setRows(filtered);
        setPage(0);
      } else {
        setRows([...all]);
        setPage(0);
      }
    };

    return (
      <div>
        {props.onAdd && (
          <TableAddButton
            className={props.addButtonClassName}
            onClick={onAdd}
            title={props.onAddBtnText || "New"}
          />
        )}
        <Paper elevation={"renderItem" in props ? 0 : 1}>
          <Box
            className="between-wrapper"
            sx={{ mt: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}
          >
            {props.onSearch && (
              <Tooltip placement="top" title={props.searchText}>
                <FormControl
                  sx={{ width: { xs: "100%", sm: "auto" }, mb: 2, ml: 1 }}
                  size="small"
                  variant="outlined"
                >
                  <InputLabel>{localized("Search")}</InputLabel>
                  <OutlinedInput
                    onKeyDown={async (e) => {
                      if (e.key === "Enter" && !loading) {
                        onSearch();
                      }
                    }}
                    onChange={(e) => (searchValue = e.target.value)}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          disabled={loading}
                          aria-label="toggle password visibility"
                          onClick={onSearch}
                          edge="end"
                        >
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Password"
                  />
                </FormControl>
              </Tooltip>
            )}

            <Box
              className="between-wrapper"
              sx={{ mb: 2, width: { xs: "100%", sm: "auto" } }}
            >
              <div className="center-wrapper">
                {!props.hideRefresh && (
                  <Tooltip placement="top" title={localized("Refresh")}>
                    <span className="margin-r-normal">
                      <IconButton disabled={loading} onClick={fetchData}>
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {props.filters && (
                  <Tooltip placement="top" title={localized("Filter list")}>
                    <span className="margin-r-normal">
                      <IconButton
                        onClick={filterRef.current?.open}
                        disabled={loading}
                      >
                        <Badge
                          color="secondary"
                          variant="dot"
                          invisible={!filterValue}
                        >
                          <FilterListIcon />
                        </Badge>
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </div>
              {"sorts" in props && (
                <Tooltip placement="top" title="Sort by">
                  <TextField
                    select
                    size="small"
                    className="margin-r-normal"
                    label={`Sort ${order}`}
                    value={orderBy}
                    onChange={(e) => setOrderBy(e.target.value)}
                    SelectProps={{
                      IconComponent: () => (
                        <span style={{ display: "inline-block" }}></span>
                      ),
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setOrder(order === "asc" ? "desc" : "asc")
                            }
                            edge="end"
                          >
                            {order === "desc" ? (
                              <KeyboardDoubleArrowDownIcon />
                            ) : (
                              <KeyboardDoubleArrowUpIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  >
                    {props.sorts.map((d) => {
                      const s = d.toString();
                      return (
                        <MenuItem key={s} value={s}>
                          {titleStr(s)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Tooltip>
              )}
            </Box>
          </Box>
          {props.filters && (
            <TableFilter<T>
              ref={filterRef}
              allData={all}
              filters={props.filters}
              onFilter={onFilter}
            />
          )}
          <TableContainer
            className="relative"
            style={{ minHeight: 200, padding: "0 2px" }}
          >
            {"headCells" in props && (
              <Table
                sx={{ minWidth: 750 }}
                stickyHeader
                aria-labelledby="tableTitle"
                size={"medium"}
              >
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headCells={props.headCells}
                  noAction={props.noAction}
                />
                <TableBody>
                  {rows &&
                    (rows.length > 0 ? (
                      stableSort<T>(rows, order, orderBy)
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => {
                          return (
                            <TableRow
                              hover
                              className="pointer"
                              onClick={(e) =>
                                props.onItemClick
                                  ? props.onItemClick(row, e)
                                  : undefined
                              }
                              tabIndex={-1}
                              key={"row_" + index}
                            >
                              {props.headCells.map((h) => {
                                const val = getValueFromName(row, h.id);
                                return (
                                  <NoBorderTableCell key={`${h.id}_${index}`}>
                                    {
                                      (h.render
                                        ? h.render(val, row, all)
                                        : val) as any
                                    }
                                  </NoBorderTableCell>
                                );
                              })}
                              {!props.noAction && (
                                <NoBorderTableCell>
                                  <TableActions
                                    onEdit={
                                      props.onEdit
                                        ? () => onEdit(row)
                                        : undefined
                                    }
                                    onDelete={
                                      props.onDelete
                                        ? () => onDelete(row)
                                        : undefined
                                    }
                                    additionalActions={
                                      props.actions
                                        ? props.actions(row)
                                        : undefined
                                    }
                                  />
                                </NoBorderTableCell>
                              )}
                            </TableRow>
                          );
                        })
                    ) : (
                      <TableRow
                        style={{
                          height: 200,
                        }}
                      >
                        <NoBorderTableCell colSpan={props.headCells.length}>
                          <NoResult />
                        </NoBorderTableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            {"renderItem" in props && (
              <Grid container spacing={3}>
                {rows &&
                  (rows.length > 0 ? (
                    stableSort<T>(rows, order, orderBy)
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        return (
                          <Grid
                            key={"row_" + row[props.uid]}
                            item
                            md={4}
                            lg={3}
                            xl={2}
                            sm={6}
                            xs={12}
                          >
                            <Paper
                              sx={{ borderRadius: "16px", overflow: "hidden" }}
                            >
                              {React.cloneElement(props.renderItem, {
                                onEditClick: () => {
                                  if (props.onEdit) {
                                    onEdit(row);
                                  }
                                },
                                onOtherClick: async () => {
                                  if (props.renderItem.props.onOtherClick) {
                                    const res =
                                      await props.renderItem.props.onOtherClick(
                                        row
                                      );
                                    if (res) {
                                      updateData(res);
                                    }
                                  }
                                },
                                onDeleteClick: async () => {
                                  await onDelete(row);
                                },
                                row: row,
                                index: index,
                              })}
                            </Paper>
                          </Grid>
                        );
                      })
                  ) : (
                    <NoResult />
                  ))}
              </Grid>
            )}
            {rows && (
              <TablePagination
                rowsPerPageOptions={
                  "headCells" in props ? [10, 25, 50, 100] : [12, 24, 48, 96]
                }
                component="div"
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    display: { sm: "flex", xs: "grid" },
                    gridTemplateColumns: "repeat(2,50%)",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    gridColumnStart: 1,
                  },
                }}
                count={rows?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={localized("Rows per page")}
              />
            )}
            {loading && (
              <div className="width-100p height-100p all-center-wrapper common-loading">
                <CircularProgress />
              </div>
            )}
          </TableContainer>
        </Paper>
      </div>
    );
  }
);

export default EnhancedTable as unknown as <T extends unknown>(
  props: (TableProps<T> | GridProps<T>) & { ref?: any }
) => JSX.Element;
